import { trackCreditsPageRedirect } from "@/analytics/tracking";
import { BeaconStyles } from "@/components/BeaconStyles/BeaconStyles";
import { PillButton } from "@/components/Button/PillButton";
import { MenuSvg, SubscriptionRestrictionSvg } from "@/components/Icon/Icon";
import { Loading } from "@/components/Loading/Loading";
import { ButtonLogo } from "@/components/Logo/Logo";
import { Menu } from "@/components/Menu/Menu";
import { NoCreditsDialog } from "@/components/NoCreditsDialog/NoCreditsDialog";
import { Profile } from "@/components/Profile/Profile";
import { SettingsDialog } from "@/components/SettingsDialog/SettingsDialog";
import { navigateToAiUsagePolicy, navigateToPrivacyPolicy, navigateToTermsOfService } from "@/components/TermsAndPolicyDialog/utils";
import { MAX_BADGE_SIZE } from "@/constants/badges";
import { POSTHOG_VISIBLE } from "@/constants/posthog";
import { useIdentifyBeacon } from "@/hooks/beacon";
import { useOrgCredits } from "@/hooks/credits";
import { Scope, usePermissions } from "@/hooks/permissions";
import { usePreventNavigationWhileUploading } from "@/hooks/upload";
import { useUser } from "@/hooks/user";
import { UploadScansModal } from "@/pages/Home/DesignSection/AssembledTabsSection/UploadScansModal/UploadScansModal";
import { DASHBOARD_HEADER_SIZE_PX, selectFullHeaderHeightPx } from "@/store/slices/dashboardSlice";
import { selectModalOpen } from "@/store/slices/modalSlice";
import { RootState } from "@/store/store";
import { Beacon, BEACON_MODAL_NAME } from "@/utils/beacon";
import { useAuth0 } from "@auth0/auth0-react";
import { ChevronLeft as ChevronLeftIcon } from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  styled,
  Theme,
  Toolbar,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React, { FC, PropsWithChildren, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

interface DashboardLink {
  name: string;
  link: string | (() => void);
  icon: React.ReactNode;
  badgeContent?: (state: RootState) => number;
  selectIsSelected?: (state: RootState) => boolean;
  requiresCredits?: boolean;
}

interface DashboardProps {
  primaryLinks: DashboardLink[];
  secondaryLinks?: DashboardLink[];
  footerLinks: DashboardLink[];
}

const CreditsButton = () => {
  const { t } = useTranslation("subscription");
  const { [Scope.UpdateFinancials]: canPurchaseCredits } = usePermissions();
  const { numCredits } = useOrgCredits({});

  return (
    <PillButton
      onClick={() => {
        if (canPurchaseCredits) {
          trackCreditsPageRedirect();
          window.open("/credits", "_blank");
        }
      }}
      variant="outlined"
      color="info"
      startIcon={<SubscriptionRestrictionSvg color="inherit" />}
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08)
      }}
    >
      {t("description.credits", { count: numCredits })}
    </PillButton>
  );
};

const ManageCredits = () => {
  const { t } = useTranslation("subscription");
  const { [Scope.UpdateFinancials]: canPurchaseCredits } = usePermissions();
  return canPurchaseCredits ? (
    <MenuItem
      id="manage-credits"
      onClick={() => {
        trackCreditsPageRedirect();
        window.open("/credits", "_blank");
      }}
      {...POSTHOG_VISIBLE}
    >
      {t("title.manageCredits")}
    </MenuItem>
  ) : (
    <></>
  );
};

const Icon = ({
  badgeContent,
  children,
  requiresCredits
}: {
  badgeContent?: (state: RootState) => number;
  children: React.ReactNode;
  requiresCredits?: boolean;
}) => {
  const badgeSelection = useSelector(badgeContent ?? (() => 0));
  const { isOutOfCredits, isLoading } = useOrgCredits({ skip: !requiresCredits });
  const theme = useTheme();

  return (
    <Badge
      variant="standard"
      badgeContent={
        !(isOutOfCredits && requiresCredits && !isLoading) ? (
          badgeSelection
        ) : (
          <SubscriptionRestrictionSvg width="100%" height="100%" color={theme.palette.info.main} />
        )
      }
      max={MAX_BADGE_SIZE}
      color={isOutOfCredits && requiresCredits && !isLoading ? "info" : "error"}
      sx={{
        "& > .MuiBadge-badge":
          isOutOfCredits && requiresCredits && !isLoading
            ? {
                marginRight: "0px !important",
                padding: "0px !important"
              }
            : {}
      }}
    >
      {children}
    </Badge>
  );
};

const NavList = ({ name, isSelected, link, badgeContent, requiresCredits, icon }: DashboardLink & { isSelected: boolean }) => {
  const navigate = useNavigate();

  return (
    <ListItem key={name} disablePadding>
      <ListItemButton
        selected={isSelected}
        sx={{
          flexDirection: "column",
          color: (theme: Theme) => (isSelected ? theme.palette.primary.main : theme.surface.onVariant),
          borderRadius: "4px"
        }}
        onClick={() => {
          if (typeof link === "string") {
            navigate(link as string);
          } else {
            link();
          }
        }}
      >
        <ListItemIcon
          sx={{
            display: "flex",
            justifyContent: "center",
            color: (theme: Theme) => (isSelected ? theme.palette.primary.main : theme.surface.onVariant)
          }}
        >
          <Icon badgeContent={badgeContent} requiresCredits={requiresCredits}>
            {icon}
          </Icon>
        </ListItemIcon>
        <ListItemText primary={name} {...POSTHOG_VISIBLE} sx={{ fontSize: { xs: "0.8em", md: "1em" } }} disableTypography />
      </ListItemButton>
    </ListItem>
  );
};

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open"
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 10
}));

const CustomDrawerDynamicWidths = (theme: Theme) => ({
  [theme.breakpoints.up("sm")]: {
    width: "80px"
  },
  [theme.breakpoints.up("md")]: {
    width: "100px"
  },
  [theme.breakpoints.up("lg")]: {
    width: "120px"
  }
});

const CustomDrawer = styled(Drawer)(({ theme }) => ({
  ...CustomDrawerDynamicWidths(theme),
  "& .MuiDrawer-paper": {
    ...CustomDrawerDynamicWidths(theme),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter
    })
  },
  flexShrink: 0,
  overflowX: "hidden",
  whiteSpace: "nowrap",
  boxSizing: "border-box"
}));

const Dashboard: FC<PropsWithChildren<DashboardProps>> = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { logout } = useAuth0();
  useIdentifyBeacon();
  const navigate = useNavigate();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const UNAVAILABLE = t("common:loading.unavailable");

  const [open, setOpen] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isBeaconOpen = useSelector((state: RootState) => selectModalOpen(state, BEACON_MODAL_NAME));
  const fullHeaderHeight = useSelector(selectFullHeaderHeightPx);

  const { picture, data: userData } = useUser();
  usePreventNavigationWhileUploading();

  const locationPath = useLocation().pathname;
  const activeLink = useSelector((state: RootState) => {
    for (const nav of [...props.primaryLinks, ...(props.secondaryLinks ?? []), ...props.footerLinks]) {
      if (typeof nav.link === "string" && locationPath.startsWith(nav.link)) {
        return nav.name;
      } else if (nav.selectIsSelected?.(state)) {
        return nav.name;
      }
    }
  });

  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleMobileDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleMobileDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: open ? "flex-end" : "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar
  }));

  const DrawerContent = (
    <Box
      component="div"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignContent: "flex-start",
        bgcolor: theme.palette.background.default
      }}
    >
      <DrawerHeader sx={{ height: `${DASHBOARD_HEADER_SIZE_PX}px` }}>
        {open && (
          <IconButton
            color="inherit"
            aria-label={t("common:label.openDrawer")}
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              display: {
                sm: "flex", // Show on small screens and up
                xs: "none" // Hide on extra small screens
              },
              justifyContent: "center",
              alignItems: "center",
              margin: 0
            }}
          >
            <MenuSvg />
          </IconButton>
        )}

        <IconButton
          onClick={handleDrawerClose}
          sx={{
            ...(!open && { display: "none" })
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </DrawerHeader>
      <List
        sx={{
          marginTop: "4px",
          paddingLeft: "8px",
          paddingRight: smDown ? "8px" : "0px",
          paddingY: smDown ? "8px" : "0px",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "8px"
        }}
      >
        {(() => {
          return props.primaryLinks?.map((e) => <NavList key={e.name} {...e} isSelected={e.name === activeLink} />);
        })()}
      </List>
      <List>{props.secondaryLinks?.map((e) => <NavList key={e.name} {...e} isSelected={e.name === activeLink} />)}</List>
      <Box sx={{ flexGrow: 1 }} component="div"></Box>
      <Box
        justifyContent={"flex-end"}
        component="div"
        sx={{
          paddingLeft: "8px",
          paddingBottom: "16px",
          paddingRight: smDown ? "8px" : undefined,
          paddingY: smDown ? "8px" : undefined
        }}
      >
        {props.footerLinks?.map((e) => <NavList key={e.name} {...e} isSelected={e.name === activeLink} />)}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: "650px" }} component="div">
      <AppBar
        position="fixed"
        open={open}
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          ".MuiToolbar-root": {
            paddingLeft: "16px",
            paddingRight: "16px",
            height: `${DASHBOARD_HEADER_SIZE_PX}px`
          }
        }}
      >
        <Toolbar sx={{ height: `${DASHBOARD_HEADER_SIZE_PX}px` }}>
          <IconButton
            aria-label={t("common:label.openDrawer")}
            onClick={handleMobileDrawerToggle}
            edge="start"
            sx={{
              display: {
                sm: "none",
                xs: "flex"
              },
              color: (theme: Theme) => theme.palette.text.primary,
              marginRight: "8px",
              marginLeft: "-4px"
            }}
          >
            <MenuSvg />
          </IconButton>
          <ButtonLogo onClick={() => navigate("/cases/all")} />
          <Box
            component="div"
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end"
            }}
          >
            <CreditsButton />
            <IconButton id="avatar-menu" onClick={handleMenuClick}>
              <Avatar
                sx={{
                  height: "36px",
                  width: "36px"
                }}
                alt={t("users:alt.profile")}
                src={picture}
              />
            </IconButton>
          </Box>
          <Menu
            innerProps={{
              id: "account-menu",
              anchorEl: anchorEl,
              open: openMenu,
              onClose: handleMenuClose,
              onClick: handleMenuClose
            }}
            noPadding
            hideArrow
          >
            <MenuItem
              disableTouchRipple
              disableRipple
              sx={{
                pointerEvents: "none", // Disable all interactions
                "&:hover": {
                  backgroundColor: "transparent" // Remove hover effect
                }
              }}
            >
              <Profile
                picture={picture ?? ""}
                name={userData?.name ?? UNAVAILABLE}
                info={
                  /*
                  role keys
                  t('role.admin')
                  t('role.owner')
                  t('role.user')
                  */
                  userData?.roles && userData.roles.length > 0
                    ? t(`users:role.${userData.roles[0].name}`, {
                        defaultValue: userData.roles[0].name
                      })
                    : UNAVAILABLE
                }
              />
            </MenuItem>
            <Divider />
            <MenuItem id="settings-button" onClick={() => setSettingsOpen(true)} {...POSTHOG_VISIBLE}>
              {t("common:title.settings")}
            </MenuItem>
            <Divider />
            <ManageCredits />
            <MenuItem onClick={navigateToTermsOfService} {...POSTHOG_VISIBLE}>
              {t("common:title.termsOfService")}
            </MenuItem>
            <MenuItem onClick={navigateToPrivacyPolicy} {...POSTHOG_VISIBLE}>
              {t("common:title.privacyPolicy")}
            </MenuItem>
            <MenuItem onClick={navigateToAiUsagePolicy} {...POSTHOG_VISIBLE}>
              {t("common:title.aiUsagePolicy")}
            </MenuItem>
            <MenuItem id="help" disabled={isBeaconOpen} onClick={() => Beacon("open")} {...POSTHOG_VISIBLE}>
              {t("title.help")}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={async () => {
                handleMenuClose();
                await logout({
                  logoutParams: { returnTo: window.location.origin }
                });
              }}
              {...POSTHOG_VISIBLE}
            >
              {t("common:button.logout")}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <CustomDrawer
        variant="permanent"
        open={true}
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiPaper-root": { borderRightWidth: "0px" }
        }}
      >
        {DrawerContent}
      </CustomDrawer>
      <Drawer
        variant="temporary"
        onTransitionEnd={handleMobileDrawerTransitionEnd}
        onClose={handleMobileDrawerClose}
        open={mobileOpen}
        ModalProps={{
          keepMounted: true
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: "120px" }
        }}
      >
        {DrawerContent}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          paddingY: 1,
          bgcolor: theme.palette.background.default,
          padding: smDown ? `${fullHeaderHeight}px 8px 8px 8px` : `${fullHeaderHeight}px 16px 16px 8px`,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxSizing: "border-box",
          overflowX: "hidden"
        }}
      >
        <Suspense fallback={<Loading />}>{props.children}</Suspense>
      </Box>
      <SettingsDialog open={settingsOpen} setOpen={setSettingsOpen} />
      <NoCreditsDialog />
      <UploadScansModal />
      <BeaconStyles />
    </Box>
  );
};

export { Dashboard };
export type { DashboardLink };
